import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { publicOrigin } from '../lib/native';
import {
  Inbox, CheckCircle2, XCircle, Loader2, Mail, Phone, Building2, Calendar, Copy,
  ExternalLink, AlertTriangle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const SuperAdminDemoRequests = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');  // pending | approved | rejected | all
  const [busyId, setBusyId] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);   // { id }
  const [rejectReason, setRejectReason] = useState('');
  const [emailHealth, setEmailHealth] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadHealth = useCallback(() => {
    axios.get(`${API}/demo-requests/email-health`, { withCredentials: true }).then((r) => setEmailHealth(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadHealth(); }, [loadHealth]);

  const sendTestEmail = async () => {
    if (!testTo.includes('@')) { toast.error('Enter a valid email address'); return; }
    setTestBusy(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/demo-requests/email-test`, { to: testTo }, { withCredentials: true });
      setTestResult(r.data);
      if (r.data.sent) toast.success(`Test email delivered to ${testTo}`);
      else toast.error('Test email failed', { description: r.data.error, duration: 12000 });
      loadHealth();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed'); } finally { setTestBusy(false); }
  };

  const sendSms = async (id) => {
    setBusyId(`sms-${id}`);
    try {
      const r = await axios.post(`${API}/demo-requests/${id}/send-sms`, {}, { withCredentials: true });
      if (r.data.sms?.sent) toast.success(`Invite texted to ${r.data.sms.to}`);
      else toast.error('SMS not sent', { description: r.data.sms?.error, duration: 10000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'SMS failed'); } finally { setBusyId(null); }
  };

  const resendInvite = async (id) => {
    setBusyId(`resend-${id}`);
    try {
      const res = await axios.post(`${API}/demo-requests/${id}/resend-invite`, {}, { withCredentials: true });
      if (res.data.email_sent) toast.success('Activation email re-sent.');
      else if (res.data.sms?.sent) toast.warning('Email failed — invite link was texted to the customer instead', { description: res.data.email_error, duration: 12000 });
      else toast.error('Email could not be delivered', { description: res.data.email_error, duration: 12000 });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setBusyId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The existing GET endpoint returns a flat list; the new POST/approve uses the
      // same docs. We compute counts client-side too for robustness.
      const res = await axios.get(`${API}/demo-requests`, { withCredentials: true });
      const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
      const counts = { all: items.length, pending: 0, approved: 0, rejected: 0 };
      items.forEach((it) => {
        const s = it.status === 'new' || !it.status ? 'pending' : it.status;
        if (counts[s] != null) counts[s] += 1;
      });
      setData({ items, counts });
    } catch (e) {
      toast.error('Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'all') return data.items;
    return data.items.filter((it) => {
      const s = it.status === 'new' || !it.status ? 'pending' : it.status;
      return s === filter;
    });
  }, [data, filter]);

  const approve = async (id) => {
    setBusyId(`approve-${id}`);
    try {
      const res = await axios.post(`${API}/demo-requests/${id}/approve`, {}, { withCredentials: true });
      if (res.data.email_sent) {
        toast.success(`Approved — activation email sent. Trial ends ${res.data.trial_ends_at?.slice(0,10)}.`);
      } else if (res.data.sms?.sent) {
        toast.warning('Approved — email failed, so the invite link was texted to the customer', {
          description: res.data.email_error, duration: 15000,
        });
      } else {
        toast.error('Approved, but the activation email was NOT delivered', {
          description: `${res.data.email_error || 'Unknown email error'} — copy the invite link below and send it to the customer manually.`,
          duration: 15000,
        });
      }
      try {
        await navigator.clipboard.writeText(res.data.invite_url);
        toast.message('Invite URL copied to clipboard');
      } catch { /* clipboard blocked */ }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approval failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    const id = rejectModal?.id;
    if (!id) return;
    setBusyId(`reject-${id}`);
    try {
      await axios.post(`${API}/demo-requests/${id}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success('Request rejected and email sent.');
      setRejectModal(null);
      setRejectReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  const StatusPill = ({ status }) => {
    const s = status === 'new' || !status ? 'pending' : status;
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      contacted: 'bg-blue-100 text-blue-700',
      qualified: 'bg-violet-100 text-violet-700',
      won: 'bg-emerald-100 text-emerald-700',
      lost: 'bg-slate-100 text-slate-700',
    }[s] || 'bg-slate-100 text-slate-700';
    return (
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${styles}`}>
        {s}
      </span>
    );
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="demo-requests-page">
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Demo Requests
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Approve new companies for a 14-day trial. Approving creates the tenant, generates a one-time invite link, and emails the admin a magic activation link. Their data is preserved even if the trial expires without subscription.
          </p>
        </header>

        {emailHealth && (
          <div className={`mb-5 rounded-md border p-4 text-sm flex gap-3 ${emailHealth.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : 'border-amber-300 bg-amber-50 text-amber-900'}`} data-testid="email-health-banner" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {emailHealth.ok ? <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 shrink-0 text-amber-600" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold">{emailHealth.ok ? `Activation emails send from ${emailHealth.sender}` : 'Activation emails are not reaching new customers'}</div>
              {!emailHealth.ok && <div className="mt-1">{emailHealth.message}</div>}
              {!emailHealth.ok && <div className="mt-1 text-xs">Until then, use <b>Copy invite link</b> (or <b>Text invite</b>) on each approved request. Unactivated customers are automatically re-emailed after 3 days.</div>}
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="email-test-controls">
                <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@yourcompany.com" data-testid="email-test-input"
                       className="px-3 py-1.5 border border-slate-300 rounded-md text-sm w-64 bg-white text-slate-900" />
                <button onClick={sendTestEmail} disabled={testBusy} data-testid="email-test-send"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#002FA7] hover:bg-[#00247D] text-white text-xs font-semibold disabled:opacity-50">
                  {testBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send test email
                </button>
                <span className="text-xs opacity-80">Use this to confirm delivery the moment your domain's DNS is verified.</span>
              </div>
              {testResult && (
                <div className={`mt-2 text-xs font-medium ${testResult.sent ? 'text-emerald-700' : 'text-red-700'}`} data-testid="email-test-result">
                  {testResult.sent ? `✓ Delivered to ${testTo} from ${testResult.sender}` : `✗ ${testResult.error}`}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 mb-5" data-testid="demo-requests-filters">
          {['pending', 'approved', 'rejected', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`demo-requests-filter-${f}`}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest border ${
                filter === f
                  ? 'bg-[#002FA7] text-white border-[#002FA7]'
                  : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {f} {data?.counts?.[f] != null && `(${data.counts[f]})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 text-sm" data-testid="demo-requests-empty">
            <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No {filter !== 'all' ? filter : ''} demo requests yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="demo-requests-list">
            {filtered.map((it) => {
              const s = it.status === 'new' || !it.status ? 'pending' : it.status;
              const isPending = s === 'pending' || s === 'contacted' || s === 'qualified';
              return (
                <div
                  key={it.id}
                  data-testid={`demo-request-row-${it.id}`}
                  className="bg-white border border-slate-200 rounded-md p-5 hover:shadow-sm transition-shadow"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <h3 className="font-bold text-slate-900 truncate">{it.company || it.company_name || '—'}</h3>
                        <StatusPill status={it.status} />
                      </div>
                      <p className="text-sm text-slate-600">
                        {it.name || it.contact_name || '—'}
                      </p>
                    </div>
                    {isPending && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => approve(it.id)}
                          disabled={busyId === `approve-${it.id}`}
                          data-testid={`demo-request-approve-${it.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                        >
                          {busyId === `approve-${it.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          Approve & start trial
                        </button>
                        <button
                          onClick={() => { setRejectModal({ id: it.id, name: it.company || it.company_name }); setRejectReason(''); }}
                          data-testid={`demo-request-reject-${it.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 hover:bg-red-50 text-slate-700 hover:text-red-700 rounded text-xs font-semibold"
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <DetailItem icon={Mail} label="Email" value={it.email} />
                    <DetailItem icon={Phone} label="Phone" value={it.phone || '—'} />
                    <DetailItem icon={Calendar} label="Submitted" value={fmtDate(it.created_at)} />
                  </div>
                  {(it.fleet_size || it.message) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs mt-3 pt-3 border-t border-slate-100">
                      {it.fleet_size && <DetailItem label="Fleet size" value={it.fleet_size} />}
                      {it.message && <DetailItem label="Notes" value={it.message} />}
                    </div>
                  )}

                  {it.status === 'approved' && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3 text-xs">
                      <div className="text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 inline-block mr-1" />
                        Approved {fmtDate(it.approved_at)}
                        {it.invite_accepted_at && ' · activated'}
                      </div>
                      {it.invite_token && !it.invite_accepted_at && (
                        <button
                          onClick={async () => {
                            const url = `${publicOrigin()}/demo-invite/${it.invite_token}`;
                            try { await navigator.clipboard.writeText(url); toast.success('Invite link copied'); }
                            catch { window.prompt('Copy this link:', url); }
                          }}
                          data-testid={`demo-request-copy-invite-${it.id}`}
                          className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
                        >
                          <Copy className="w-3 h-3" /> Copy invite link
                        </button>
                      )}
                      {!it.invite_accepted_at && (
                        <button
                          onClick={() => resendInvite(it.id)}
                          disabled={busyId === `resend-${it.id}`}
                          data-testid={`demo-request-resend-invite-${it.id}`}
                          className="inline-flex items-center gap-1 text-[#002FA7] hover:underline disabled:opacity-50"
                        >
                          {busyId === `resend-${it.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Resend activation email
                        </button>
                      )}
                      {!it.invite_accepted_at && it.phone && (
                        <button
                          onClick={() => sendSms(it.id)}
                          disabled={busyId === `sms-${it.id}`}
                          data-testid={`demo-request-send-sms-${it.id}`}
                          className="inline-flex items-center gap-1 text-[#002FA7] hover:underline disabled:opacity-50"
                        >
                          {busyId === `sms-${it.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Phone className="w-3 h-3" />} Text invite
                        </button>
                      )}
                      {!it.invite_accepted_at && it.invite_sms_status && (
                        <span data-testid={`demo-request-sms-status-${it.id}`} title={it.invite_sms_error || ''}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${it.invite_sms_status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {it.invite_sms_status === 'sent' ? <><CheckCircle2 className="w-3 h-3" /> SMS sent</> : <><AlertTriangle className="w-3 h-3" /> SMS failed</>}
                        </span>
                      )}
                      {!it.invite_accepted_at && it.invite_reminder_count > 0 && (
                        <span className="text-slate-500" data-testid={`demo-request-reminders-${it.id}`}>
                          {it.invite_reminder_count} auto-reminder{it.invite_reminder_count !== 1 ? 's' : ''} · last {fmtDate(it.invite_reminder_at)}
                        </span>
                      )}
                      {!it.invite_accepted_at && it.invite_email_status === 'sent' && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest" data-testid={`demo-request-email-tracker-${it.id}`}>
                          {['delivered', 'opened', 'clicked'].map((k) => {
                            const at = it.invite_email_events?.[k];
                            return (
                              <span key={k} title={at ? `${k} ${fmtDate(at)}` : `not ${k} yet`}
                                    className={`px-1.5 py-0.5 rounded ${at ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                                {at ? '✓ ' : '· '}{k}
                              </span>
                            );
                          })}
                          {!it.invite_email_tracking && <span className="text-slate-400 normal-case tracking-normal font-normal" title="Needs the Resend webhook configured (RESEND_WEBHOOK_SECRET + resend.com/webhooks → /api/webhooks/resend)">awaiting webhook</span>}
                        </span>
                      )}
                      {!it.invite_accepted_at && it.invite_email_status && (
                        <span
                          data-testid={`demo-request-email-status-${it.id}`}
                          title={it.invite_email_error || ''}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${it.invite_email_status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}
                        >
                          {it.invite_email_status === 'sent' ? <><CheckCircle2 className="w-3 h-3" /> Email delivered</> : <><AlertTriangle className="w-3 h-3" /> Email failed</>}
                        </span>
                      )}
                      {!it.invite_accepted_at && it.invite_email_status === 'failed' && it.invite_email_error && (
                        <div className="w-full text-xs text-red-700 mt-1" data-testid={`demo-request-email-error-${it.id}`}>{it.invite_email_error}</div>
                      )}
                    </div>
                  )}
                  {it.status === 'rejected' && it.rejection_reason && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-red-700">
                      <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                      Rejected: {it.rejection_reason}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="demo-reject-modal">
          <div className="bg-white rounded-md max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Reject demo request
            </h3>
            <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              We'll email the requester a polite rejection. Optionally include a reason.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="demo-reject-reason"
              rows={3}
              placeholder="Optional reason (sent in the email)"
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setRejectModal(null)}
                className="px-3 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900"
                data-testid="demo-reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={reject}
                disabled={!!busyId}
                data-testid="demo-reject-confirm"
                className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-semibold disabled:opacity-50"
              >
                {busyId ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                Reject request
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="text-slate-700">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
    <p className="flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3 text-slate-400" />}
      <span className="truncate">{value}</span>
    </p>
  </div>
);

export default SuperAdminDemoRequests;
